import { useState } from "react";
import { Download, ExternalLink, FileText, ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadLegalDocument, uploadMenuImports } from "@/api/client";
import { toast } from "@/components/ui/sonner";

const MENU_IMPORT_ACCEPT = ".pdf,.xlsx,.xls,.csv,.txt,.jpg,.jpeg,.png,.webp,.heic,.heif";

export const MENU_IMPORT_SAMPLES = [
  { label: "CSV", href: "/onboarding-samples/sample-menu.csv", openInNewTab: false },
  { label: "Excel (XLSX)", href: "/onboarding-samples/sample-menu.xlsx", openInNewTab: false },
  { label: "PDF", href: "/onboarding-samples/sample-menu.pdf", openInNewTab: true },
  { label: "PNG photo", href: "/onboarding-samples/sample-menu.png", openInNewTab: true },
  { label: "TXT", href: "/onboarding-samples/sample-menu.txt", openInNewTab: false },
];

export function MenuImportSamples({ testIdPrefix = "menu-import" }) {
  return (
    <div className="mt-4 rounded-lg border bg-white p-3" data-testid={`${testIdPrefix}-samples`}>
      <p className="text-xs font-extrabold uppercase text-muted-foreground">SEE THE EXPECTED FORMAT</p>
      <p className="mt-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-photo-hint`}>
        Download a template below to see how your file should be structured, then fill it in with your own items
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {MENU_IMPORT_SAMPLES.map((sample) => (
          <a
            key={sample.href}
            href={sample.href}
            {...(sample.openInNewTab
              ? { target: "_blank", rel: "noopener noreferrer" }
              : { download: true })}
            data-testid={`${testIdPrefix}-sample-${sample.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="inline-flex items-center gap-1 rounded-full border bg-[#F8F5EE] px-3 py-1.5 text-xs font-bold text-[#173B2F] hover:border-[#EA6A3B]"
          >
            {sample.openInNewTab ? <ExternalLink className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
            {sample.label}
          </a>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-photo-hint`}>
        CSV, Excel, and TXT need the same columns as the template. PDF and PNG are scanned automatically — just make sure item names, descriptions, and prices are clearly listed.
      </p>
      <a
        href="/onboarding-samples/sample-menu.png"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testIdPrefix}-sample-image-link`}
        className="mt-3 block overflow-hidden rounded-lg border hover:border-[#EA6A3B]"
      >
        <img
          src="/onboarding-samples/sample-menu.png"
          alt="Sample menu photo for import — open in new tab"
          className="aspect-[16/7] w-full object-cover object-top"
          data-testid={`${testIdPrefix}-sample-image`}
        />
      </a>
    </div>
  );
}

function mapDraftProducts(items = []) {
  return items.map((item) => ({
    name: item.name,
    description: item.description || "",
    price: Number(item.price || 0),
    category: item.category_name || item.category || "Imported",
  }));
}

export function MenuImportPanel({ onImported, testIdPrefix = "menu-import" }) {
  const [importing, setImporting] = useState(false);

  const handleImport = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setImporting(true);
    try {
      const result = await uploadMenuImports(files);
      const products = mapDraftProducts(result.draft_products || []);
      onImported(products, result);
      const fileCount = result.summary?.source_file_count || files.length;
      toast.success(`${products.length} products imported from ${fileCount} file${fileCount === 1 ? "" : "s"}`);
      const failed = result._failures || [];
      if (failed.length) {
        toast.message(`${failed.length} file${failed.length === 1 ? "" : "s"} could not be parsed`, {
          description: failed.map((item) => item.filename).join(", "),
        });
      }
    } catch (error) {
      const detail = error.response?.data?.detail?.error;
      toast.error(detail?.message || "Could not parse these menu files", {
        description: detail?.code === "IMPORT_OCR_FAILED"
          ? "We couldn’t read this scanned file. Try a clearer photo, or upload CSV/TXT/XLSX instead."
          : undefined,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="rounded-xl border bg-[#F8F5EE] p-5" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-5 w-5 text-[#EA6A3B]" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-extrabold" data-testid={`${testIdPrefix}-title`}>Upload menu file or photo</p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-body`}>
            PDF, Excel (XLSX), CSV, TXT, or menu photos (JPG/PNG/WebP). You can select multiple files — we parse each one and combine the items.
          </p>
        </div>
      </div>

      <label
        data-testid={`${testIdPrefix}-dropzone`}
        className={`mt-4 grid min-h-28 cursor-pointer place-items-center rounded-xl border-2 border-dashed p-5 text-center transition-colors ${importing ? "border-[#EA6A3B]/60 bg-[#FFF3EC]" : "border-[#EA6A3B]/35 bg-white hover:border-[#EA6A3B] hover:bg-[#FFF7F2]"}`}
      >
        <input
          data-testid={`${testIdPrefix}-file-input`}
          type="file"
          accept={MENU_IMPORT_ACCEPT}
          multiple
          className="sr-only"
          disabled={importing}
          onChange={handleImport}
        />
        {importing ? <Loader2 className="h-8 w-8 animate-spin text-[#EA6A3B]" /> : <ImagePlus className="h-8 w-8 text-[#EA6A3B]" />}
        <span className="mt-2 text-sm font-bold text-[#173B2F]" data-testid={`${testIdPrefix}-cta`}>
          {importing ? "Parsing menu files…" : "Choose one or more menu files"}
        </span>
        <span className="mt-1 text-xs text-muted-foreground">Max 8MB per file · multiple files supported</span>
      </label>

      <MenuImportSamples testIdPrefix={testIdPrefix} />
    </section>
  );
}

export const LEGAL_DOCUMENT_TYPES = [
  {
    id: "privacy_policy",
    label: "Privacy policy",
    hint: "How you collect and use customer data.",
    template: "/onboarding-samples/privacy-policy-template.txt",
  },
  {
    id: "terms_and_conditions",
    label: "Terms & conditions",
    hint: "Rules for ordering from your storefront.",
    template: "/onboarding-samples/terms-and-conditions-template.txt",
  },
  {
    id: "cookie_policy",
    label: "Cookie policy",
    hint: "Required if you use analytics or marketing cookies.",
  },
  {
    id: "impressum",
    label: "Impressum",
    hint: "Required in Germany, Austria, and Switzerland.",
    template: "/onboarding-samples/impressum-template.txt",
  },
  {
    id: "other",
    label: "Other regional document",
    hint: "Any additional document your lawyer or region requires.",
  },
];

export function LegalDocumentUploads({ documents, onChange, testIdPrefix = "legal-doc" }) {
  const [uploadingId, setUploadingId] = useState(null);

  const uploadDocument = async (docType, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploadingId(docType.id);
    try {
      const uploaded = await uploadLegalDocument(docType.id, file);
      onChange({
        ...documents,
        [docType.id]: {
          url: uploaded.url,
          original_filename: uploaded.original_filename || file.name,
          content_type: uploaded.content_type || file.type,
          uploaded_at: new Date().toISOString(),
          content_text: uploaded.content_text || "",
        },
      });
      toast.success(`${docType.label} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || `Could not upload ${docType.label.toLowerCase()}`);
    } finally {
      setUploadingId(null);
    }
  };

  const removeDocument = (docTypeId) => {
    const next = { ...documents };
    delete next[docTypeId];
    onChange(next);
  };

  return (
    <div className="mt-8 grid gap-4" data-testid={`${testIdPrefix}-list`}>
      {LEGAL_DOCUMENT_TYPES.map((docType) => {
        const uploaded = documents?.[docType.id];
        const busy = uploadingId === docType.id;
        return (
          <article key={docType.id} className="rounded-xl border bg-white p-4" data-testid={`${testIdPrefix}-${docType.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-extrabold" data-testid={`${testIdPrefix}-${docType.id}-label`}>{docType.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{docType.hint}</p>
                {docType.template ? (
                  <a
                    href={docType.template}
                    download
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-primary"
                    data-testid={`${testIdPrefix}-${docType.id}-template-link`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download starter template
                  </a>
                ) : null}
              </div>
              {uploaded && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  data-testid={`${testIdPrefix}-${docType.id}-remove-button`}
                  onClick={() => removeDocument(docType.id)}
                >
                  Remove
                </Button>
              )}
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed bg-[#F8F5EE] px-4 py-3 hover:border-[#EA6A3B]" data-testid={`${testIdPrefix}-${docType.id}-upload-label`}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="sr-only"
                disabled={busy}
                data-testid={`${testIdPrefix}-${docType.id}-input`}
                onChange={(event) => uploadDocument(docType, event)}
              />
              {busy ? <Loader2 className="h-4 w-4 animate-spin text-[#EA6A3B]" /> : <FileText className="h-4 w-4 text-[#EA6A3B]" />}
              <span className="text-sm font-bold">
                {uploaded ? `Uploaded: ${uploaded.original_filename}` : "Upload PDF or Word document"}
              </span>
            </label>
          </article>
        );
      })}
    </div>
  );
}
